import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  HeartPulse, Languages, CalendarClock, Clock, ListChecks, MessageSquare, Sparkles,
  ClipboardList, Users, Mail, Bot, Building2, ScanLine, ShieldCheck, Globe, Smartphone,
  ArrowRight, Check, Loader2, Menu, X, BellRing,
} from "lucide-react";

const CARE_IMG = "https://images.unsplash.com/photo-1587556930720-58ec521056a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const WARM_IMG = "https://images.unsplash.com/photo-1589061434060-a05a5335bfbb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const C = {
  fr: {
    nav: { features: "Fonctionnalités", modules: "Modules", ai: "Intelligence artificielle", contact: "Contact", login: "Se connecter" },
    hero: {
      eyebrow: "Plateforme de gestion pour résidences · Québec",
      title: "Toute votre résidence, réunie en un seul endroit.",
      sub: "Residence Connect regroupe horaires, tâches, soins aux résidents, communication d'équipe et intelligence artificielle dans une plateforme bilingue pensée pour les RI, RTF et RPA.",
      cta1: "Demander une démo", cta2: "Se connecter",
      trust: "Conçu spécifiquement pour les résidences intermédiaires, RTF et RPA du Québec.",
    },
    stats: [
      { v: "12+", l: "modules intégrés" },
      { v: "FR / EN", l: "interface bilingue" },
      { v: "Multi-centres", l: "gestion centralisée" },
      { v: "IA", l: "assistants intégrés" },
    ],
    featTitle: "Une plateforme, tous vos besoins",
    featSub: "Chaque outil dont votre équipe a besoin au quotidien, sans jongler entre dix logiciels.",
    features: [
      { icon: CalendarClock, t: "Planification des horaires", d: "Vue jour / semaine / mois, glisser-déposer, quarts ouverts, copie de semaine et codes de couleur." },
      { icon: Clock, t: "Pointeuse & présence", d: "Pointage d'arrivée et de départ avec géolocalisation optionnelle et suivi des périodes de paie." },
      { icon: ListChecks, t: "Tâches & listes de contrôle", d: "Tâches récurrentes, preuves photo et suivi d'achèvement en temps réel." },
      { icon: MessageSquare, t: "Messagerie d'équipe", d: "Discussions de groupe et privées, pièces jointes, accusés de lecture et épinglage." },
      { icon: ClipboardList, t: "Grilles de cotation", d: "Suivez selles, sommeil, comportement et plus, avec codes personnalisés, légendes et exports PDF/CSV." },
      { icon: Users, t: "Résidents & soins", d: "Dossiers des résidents, tâches de soins quotidiennes et instructions de soins centralisées." },
      { icon: Mail, t: "CRM courriel multi-boîtes", d: "Connectez Gmail et Outlook, triez automatiquement les courriels par IA et convertissez-les en tâches." },
      { icon: ClipboardList, t: "Rapports de fin de quart", d: "Rapports de quart avec résumés générés par IA, signalement des urgences et suivi." },
      { icon: ScanLine, t: "Numériseur de documents", d: "Numérisez et partagez des documents directement depuis la messagerie de l'équipe." },
      { icon: Building2, t: "Multi-centres", d: "Gérez plusieurs résidences avec des accès dédiés pour chaque propriétaire et équipe." },
      { icon: BellRing, t: "Notifications en temps réel", d: "Alertes instantanées pour les messages, tâches urgentes et événements importants." },
      { icon: ShieldCheck, t: "Rôles & sécurité", d: "Contrôle d'accès strict par rôle : administrateur, superviseur et employé." },
    ],
    aiEyebrow: "Intelligence artificielle",
    aiTitle: "Une IA qui connaît vraiment votre résidence",
    aiSub: "Posez une question en français ou en anglais et obtenez une réponse instantanée, tirée de vos vraies données.",
    aiPoints: [
      "« Quand a eu lieu la dernière crise de M. Tremblay ? » — l'assistant cherche dans vos grilles de cotation.",
      "Résumés automatiques des discussions de groupe et des rapports de fin de quart.",
      "Tri intelligent des courriels entrants par urgence et catégorie.",
      "Tendances et comptes sur une période : cette semaine vs la semaine dernière.",
    ],
    gridEyebrow: "Notre différence",
    gridTitle: "Les grilles de cotation, enfin numériques",
    gridSub: "Remplacez vos feuilles papier par un suivi structuré, consultable et exportable — directement lié à la messagerie et à l'IA.",
    gridPoints: [
      "Codes et légendes personnalisés pour chaque résident.",
      "Saisie rapide depuis le chat avec @résident.",
      "Exports PDF / CSV et journal d'audit complet.",
      "Consultable instantanément par l'assistant IA.",
    ],
    whyTitle: "Pourquoi les résidences choisissent Residence Connect",
    why: [
      { icon: Globe, t: "Entièrement bilingue", d: "Interface complète en français et en anglais, avec le français par défaut." },
      { icon: Smartphone, t: "Pensé pour le mobile", d: "Vos équipes accèdent à tout depuis leur téléphone, sur le terrain." },
      { icon: ShieldCheck, t: "Sécurisé & multi-centres", d: "Isolation des données par centre et contrôle d'accès par rôle." },
      { icon: Sparkles, t: "Propulsé par l'IA", d: "Des assistants intelligents qui font gagner du temps à votre équipe chaque jour." },
    ],
    contactEyebrow: "Parlons-en",
    contactTitle: "Découvrez Residence Connect en action",
    contactSub: "Laissez-nous vos coordonnées et notre équipe vous contactera pour une démonstration personnalisée.",
    form: { name: "Nom complet", org: "Nom de la résidence", email: "Courriel", phone: "Téléphone (optionnel)", message: "Votre message", send: "Envoyer la demande", sending: "Envoi…", ok: "Merci ! Nous vous contacterons bientôt.", err: "Une erreur est survenue. Réessayez." },
    footer: "Gestion de résidence intelligente · Québec",
  },
  en: {
    nav: { features: "Features", modules: "Modules", ai: "Artificial Intelligence", contact: "Contact", login: "Log in" },
    hero: {
      eyebrow: "Residence management platform · Québec",
      title: "Your entire residence, together in one place.",
      sub: "Residence Connect brings scheduling, tasks, resident care, team communication and artificial intelligence into one bilingual platform built for intermediate residences, RTF and RPA.",
      cta1: "Request a demo", cta2: "Log in",
      trust: "Purpose-built for Québec intermediate residences, RTF and RPA.",
    },
    stats: [
      { v: "12+", l: "built-in modules" },
      { v: "FR / EN", l: "bilingual interface" },
      { v: "Multi-center", l: "centralized management" },
      { v: "AI", l: "built-in assistants" },
    ],
    featTitle: "One platform, everything you need",
    featSub: "Every tool your team needs day to day — without juggling ten different apps.",
    features: [
      { icon: CalendarClock, t: "Smart scheduling", d: "Day / week / month views, drag-and-drop, open shifts, copy week and color coding." },
      { icon: Clock, t: "Time clock & presence", d: "Clock in and out with optional geolocation and pay-period tracking." },
      { icon: ListChecks, t: "Tasks & checklists", d: "Recurring tasks, photo proof and real-time completion tracking." },
      { icon: MessageSquare, t: "Team messaging", d: "Group and private chats, attachments, read receipts and pinning." },
      { icon: ClipboardList, t: "Tracking grids", d: "Track stool, sleep, behavior and more with custom codes, legends and PDF/CSV exports." },
      { icon: Users, t: "Residents & care", d: "Resident records, daily care tasks and centralized care instructions." },
      { icon: Mail, t: "Multi-inbox email CRM", d: "Connect Gmail and Outlook, auto-triage emails with AI and turn them into tasks." },
      { icon: ClipboardList, t: "End-of-shift reports", d: "Shift reports with AI-generated summaries, urgency flagging and follow-up." },
      { icon: ScanLine, t: "Document scanner", d: "Scan and share documents right from your team messaging." },
      { icon: Building2, t: "Multi-center", d: "Run multiple residences with dedicated access for each owner and team." },
      { icon: BellRing, t: "Real-time notifications", d: "Instant alerts for messages, urgent tasks and important events." },
      { icon: ShieldCheck, t: "Roles & security", d: "Strict role-based access control: admin, supervisor and employee." },
    ],
    aiEyebrow: "Artificial Intelligence",
    aiTitle: "An AI that truly knows your residence",
    aiSub: "Ask a question in French or English and get an instant answer, drawn from your real data.",
    aiPoints: [
      "\"When was Mr. Tremblay's last crisis?\" — the assistant searches your tracking grids.",
      "Automatic summaries of group discussions and end-of-shift reports.",
      "Smart triage of incoming emails by urgency and category.",
      "Trends and counts over a period: this week vs last week.",
    ],
    gridEyebrow: "What sets us apart",
    gridTitle: "Tracking grids, finally digital",
    gridSub: "Replace paper sheets with structured, searchable, exportable tracking — wired directly into messaging and AI.",
    gridPoints: [
      "Custom codes and legends for each resident.",
      "Quick entry from chat with @resident.",
      "PDF / CSV exports and a full audit log.",
      "Instantly searchable by the AI assistant.",
    ],
    whyTitle: "Why residences choose Residence Connect",
    why: [
      { icon: Globe, t: "Fully bilingual", d: "Complete interface in French and English, with French by default." },
      { icon: Smartphone, t: "Mobile-first", d: "Your teams access everything from their phone, on the floor." },
      { icon: ShieldCheck, t: "Secure & multi-center", d: "Per-center data isolation and role-based access control." },
      { icon: Sparkles, t: "Powered by AI", d: "Smart assistants that save your team time every single day." },
    ],
    contactEyebrow: "Let's talk",
    contactTitle: "See Residence Connect in action",
    contactSub: "Leave us your details and our team will reach out for a personalized demo.",
    form: { name: "Full name", org: "Residence name", email: "Email", phone: "Phone (optional)", message: "Your message", send: "Send request", sending: "Sending…", ok: "Thank you! We'll be in touch soon.", err: "Something went wrong. Please try again." },
    footer: "Smart residence management · Québec",
  },
};

const Logo = ({ dark }) => (
  <div className="flex items-center gap-2.5">
    <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shrink-0">
      <HeartPulse className="w-5 h-5 text-white" />
    </div>
    <span className={`font-display font-extrabold text-lg ${dark ? "text-white" : "text-foreground"}`}>Residence Connect</span>
  </div>
);

export default function Landing() {
  const { lang, toggleLang } = useT();
  const c = C[lang] || C.fr;
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [form, setForm] = useState({ name: "", organization: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSending(true);
    try {
      await api.post("/leads", { ...form, lang });
      toast.success(c.form.ok);
      setForm({ name: "", organization: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || c.form.err);
    } finally { setSending(false); }
  };

  const navLinks = [
    { id: "features", label: c.nav.features },
    { id: "ai", label: c.nav.ai },
    { id: "modules", label: c.nav.modules },
    { id: "contact", label: c.nav.contact },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* ---------- Nav ---------- */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-background/85 backdrop-blur-lg border-b border-border" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={() => scrollTo("top")} data-testid="landing-logo"><Logo dark={!scrolled} /></button>
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} data-testid={`nav-${l.id}`}
                className={`text-sm font-medium transition-colors ${scrolled ? "text-muted-foreground hover:text-foreground" : "text-white/75 hover:text-white"}`}>{l.label}</button>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={toggleLang} data-testid="landing-lang-toggle"
              className={`flex items-center gap-1.5 text-sm transition-colors ${scrolled ? "text-muted-foreground hover:text-foreground" : "text-white/75 hover:text-white"}`}>
              <Languages className="w-4 h-4" />{lang === "fr" ? "EN" : "FR"}
            </button>
            <Link to="/login"><Button size="sm" className="rounded-full px-5" data-testid="landing-login-btn">{c.nav.login}</Button></Link>
            <button className={`md:hidden ml-1 transition-colors ${scrolled ? "text-foreground" : "text-white"}`} onClick={() => setMenuOpen((v) => !v)} data-testid="landing-menu-toggle">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-background border-b border-border px-5 pb-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-left py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground">{l.label}</button>
            ))}
          </div>
        )}
      </header>

      {/* ---------- Hero ---------- */}
      <section id="top" className="relative bg-sidebar sidebar-noise text-sidebar-foreground overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[38rem] h-[38rem] rounded-full bg-primary/25 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[34rem] h-[34rem] rounded-full bg-primary/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 lg:pt-40 lg:pb-32 grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1.5">
              <HeartPulse className="w-3.5 h-3.5" />{c.hero.eyebrow}
            </span>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.05] mt-6 text-4xl sm:text-5xl lg:text-6xl">
              {c.hero.title}
            </h1>
            <p className="text-sidebar-muted text-lg mt-6 max-w-xl leading-relaxed">{c.hero.sub}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" onClick={() => scrollTo("contact")} className="rounded-full h-12 px-7 text-base" data-testid="hero-cta-demo">
                {c.hero.cta1}<ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 text-base bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white" data-testid="hero-cta-login">
                  {c.hero.cta2}
                </Button>
              </Link>
            </div>
            <p className="text-sidebar-muted/80 text-sm mt-6 flex items-center gap-2">
              <Check className="w-4 h-4 text-primary" />{c.hero.trust}
            </p>
          </div>

          {/* App preview mockup */}
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-3 shadow-2xl">
              <div className="rounded-xl bg-background overflow-hidden">
                <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-border">
                  <span className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                  <span className="ml-3 text-[11px] text-muted-foreground">residenceconnect.ca</span>
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Bot className="w-4 h-4 text-primary" />
                    <span className="text-xs font-semibold text-foreground">{lang === "fr" ? "Assistant IA" : "AI Assistant"}</span>
                  </div>
                  <div className="rounded-lg bg-primary text-white text-xs px-3 py-2 ml-auto w-fit max-w-[85%]">
                    {lang === "fr" ? "Quand était la dernière crise de Mme Allard ?" : "When was Mrs. Allard's last crisis?"}
                  </div>
                  <div className="rounded-lg bg-muted text-foreground text-xs px-3 py-2 w-fit max-w-[90%] border border-border">
                    {lang === "fr" ? "La dernière crise remonte au 28 juil. à 13h15 — 2 crises au total cette semaine." : "The last crisis was on Jul 28 at 1:15 PM — 2 crises total this week."}
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-1">
                    {[CalendarClock, MessageSquare, ClipboardList].map((Ic, i) => (
                      <div key={i} className="rounded-lg border border-border p-2.5 flex flex-col items-center gap-1">
                        <Ic className="w-4 h-4 text-primary" />
                        <span className="w-full h-1.5 rounded-full bg-muted" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Stats strip ---------- */}
      <section className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {c.stats.map((s, i) => (
            <div key={i} className="text-center md:text-left" data-testid="landing-stat">
              <p className="font-display text-2xl sm:text-3xl font-extrabold text-primary">{s.v}</p>
              <p className="text-sm text-muted-foreground mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- Features grid ---------- */}
      <section id="features" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">{c.nav.features}</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">{c.featTitle}</h2>
          <p className="text-muted-foreground text-lg mt-4">{c.featSub}</p>
        </div>
        <div id="modules" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {c.features.map((f, i) => (
            <div key={i} data-testid="feature-card"
              className="group rounded-xl border border-border bg-card p-6 hover:border-primary/40 hover:-translate-y-0.5 transition-[transform,border-color] duration-200">
              <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                <f.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-display font-semibold text-lg">{f.t}</h3>
              <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------- AI spotlight ---------- */}
      <section id="ai" className="bg-sidebar sidebar-noise text-sidebar-foreground">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1 relative">
            <div className="absolute -inset-4 bg-primary/15 blur-3xl rounded-full" />
            <img src={CARE_IMG} alt="Care documentation" loading="lazy"
              className="relative rounded-2xl border border-white/10 object-cover w-full h-[360px] shadow-2xl" />
          </div>
          <div className="order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary bg-primary/10 border border-primary/20 rounded-full px-3 py-1.5">
              <Sparkles className="w-3.5 h-3.5" />{c.aiEyebrow}
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-5">{c.aiTitle}</h2>
            <p className="text-sidebar-muted text-lg mt-4">{c.aiSub}</p>
            <ul className="mt-7 space-y-3">
              {c.aiPoints.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-sidebar-foreground/90">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-primary" /></span>
                  <span className="text-[15px] leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- Tracking grids spotlight ---------- */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">{c.gridEyebrow}</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">{c.gridTitle}</h2>
          <p className="text-muted-foreground text-lg mt-4">{c.gridSub}</p>
          <ul className="mt-7 space-y-3">
            {c.gridPoints.map((p, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-emerald-600" /></span>
                <span className="text-[15px] leading-relaxed text-foreground">{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="w-5 h-5 text-emerald-600" />
            <span className="font-display font-semibold">{lang === "fr" ? "Grille · Comportement" : "Grid · Behavior"}</span>
          </div>
          <div className="space-y-2.5">
            {[
              { c: "C", m: lang === "fr" ? "Crise" : "Crisis", t: "28 · 13:15", u: "Marie" },
              { c: "N", m: lang === "fr" ? "Normal" : "Normal", t: "27 · 09:00", u: "Léa" },
              { c: "A", m: "Agitation", t: "26 · 21:30", u: "Marc" },
            ].map((r, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5">
                <span className="w-9 h-9 rounded-lg bg-emerald-500/15 text-emerald-700 flex items-center justify-center font-bold">{r.c}</span>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium">{r.m}</p><p className="text-[11px] text-muted-foreground">{r.t} · {r.u}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Why ---------- */}
      <section className="bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-center max-w-2xl mx-auto">{c.whyTitle}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {c.why.map((w, i) => (
              <div key={i} className="rounded-xl border border-border bg-background p-6" data-testid="why-card">
                <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4"><w.icon className="w-5 h-5 text-primary" /></div>
                <h3 className="font-display font-semibold">{w.t}</h3>
                <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Contact ---------- */}
      <section id="contact" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-primary">{c.contactEyebrow}</span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">{c.contactTitle}</h2>
          <p className="text-muted-foreground text-lg mt-4">{c.contactSub}</p>
          <div className="relative mt-10 rounded-2xl overflow-hidden border border-border">
            <img src={WARM_IMG} alt="Caregiver and resident" loading="lazy" className="w-full h-56 object-cover" />
          </div>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-4" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">{c.form.name}</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" data-testid="contact-name" />
            </div>
            <div>
              <label className="text-sm font-medium">{c.form.org}</label>
              <Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="mt-1.5" data-testid="contact-org" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">{c.form.email}</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" data-testid="contact-email" />
            </div>
            <div>
              <label className="text-sm font-medium">{c.form.phone}</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="contact-phone" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">{c.form.message}</label>
            <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 resize-none" data-testid="contact-message" />
          </div>
          <Button type="submit" size="lg" disabled={sending} className="w-full h-12 rounded-full text-base" data-testid="contact-submit">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{c.form.sending}</> : c.form.send}
          </Button>
        </form>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="bg-sidebar sidebar-noise text-sidebar-foreground">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <Logo dark />
          <div className="flex items-center gap-6">
            {navLinks.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} className="text-sm text-sidebar-muted hover:text-white transition-colors">{l.label}</button>
            ))}
          </div>
          <p className="text-sidebar-muted text-sm">© 2026 Residence Connect · {c.footer}</p>
        </div>
      </footer>
    </div>
  );
}
